import React, { useEffect, useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { 
  Box, 
  Typography, 
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import { useApi } from '../services/api';

const Dashboard: React.FC = () => {
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();
  const { getAuthenticatedApi } = useApi();
  const { getToken, isLoaded: authLoaded } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [configurations, setConfigurations] = useState<any[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [generatedConfig, setGeneratedConfig] = useState<any>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editableConfig, setEditableConfig] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    vendor: 'cisco',
    switch_type: 'edge',
    version: '1.0',
    description: '',
    template_content: ''
  });

  useEffect(() => {
    // Wait for Clerk to be fully loaded before making API calls
    if (authLoaded && userLoaded) {
      checkBackendConnection();
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoaded, userLoaded]);

  const checkBackendConnection = async () => {
    try {
      const api = await getAuthenticatedApi();
      await api.healthCheck();
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Backend connection failed:', error);
      setConnectionStatus('error');
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await getAuthenticatedApi();
      
      // Load templates
      const templatesResponse = await api.listTemplates();
      // Handle both array and object responses
      const templateArray = Array.isArray(templatesResponse) ? templatesResponse : (templatesResponse?.templates || []);
      setTemplates(templateArray);
      
      // Load configurations
      const configsResponse = await api.listConfigurations();
      // Handle both array and object responses
      const configArray = Array.isArray(configsResponse) ? configsResponse : (configsResponse?.configurations || []);
      setConfigurations(configArray);
    } catch (error: any) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Please try signing out and back in.');
      } else if (error.response?.status === 403) {
        console.warn('Access denied - some features may be limited');
        // Don't show error for 403, just log it
      } else if (error.response?.status === 404) {
        console.warn('Some endpoints not found - running in limited mode');
        // Don't show error for 404, just log it
      } else {
        setError(error.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const testToken = async () => {
    try {
      const token = await getToken();
      console.log('Clerk token:', token);
      if (token) {
        // Try to decode it to see the claims
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          console.log('Token payload:', payload);
        }
      }
    } catch (error) {
      console.error('Error getting token:', error);
    }
  };

  const testGenerateConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await getAuthenticatedApi();
      const result = await api.generateConfiguration({
        switch_name: 'TEST-SWITCH-01',
        vendor: 'cisco',
        vlan_id: 10,
        ip_address: '192.168.10.1',
        subnet_mask: '255.255.255.0',
        gateway: '192.168.10.254'
      });
      alert('Configuration generated successfully!\n\n' + result.configuration);
      loadData(); // Reload data
    } catch (error: any) {
      console.error('Error generating config:', error);
      setError(error.message || 'Failed to generate configuration');
    } finally {
      setLoading(false);
    }
  };

  const generateFromNaturalLanguage = async () => {
    if (!naturalLanguageInput.trim()) {
      setError('Please enter a configuration request');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const api = await getAuthenticatedApi();
      const result = await api.generateFromNaturalLanguage(naturalLanguageInput);
      setGeneratedConfig(result);
      setEditableConfig(result.configuration);
      setIsEditMode(false);
      setConfigDialogOpen(true);
      // Don't reload data yet - wait until user saves
    } catch (error: any) {
      console.error('Error generating config from natural language:', error);
      setError(error.message || 'Failed to generate configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    setIsSaving(true);
    try {
      const api = await getAuthenticatedApi();
      await api.saveConfiguration({
        switch_name: generatedConfig.metadata?.switch_name || 'SWITCH-01',
        vendor: generatedConfig.metadata?.vendor || 'cisco',
        configuration: editableConfig,
        natural_language_request: generatedConfig.metadata?.natural_language_request,
        parsed_parameters: generatedConfig.parsed_parameters
      });
      
      setConfigDialogOpen(false);
      setNaturalLanguageInput('');
      setGeneratedConfig(null);
      setEditableConfig('');
      setIsEditMode(false);
      loadData(); // Reload configurations list after saving
      
      // Show success message
      setError(null);
      alert('Configuration saved successfully!');
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      setError(error.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setConfigDialogOpen(false);
    setGeneratedConfig(null);
    setEditableConfig('');
    setIsEditMode(false);
  };

  const viewTemplate = async (templateId: string) => {
    setLoading(true);
    try {
      const api = await getAuthenticatedApi();
      const template = await api.getTemplate(templateId);
      setSelectedTemplate(template);
      setTemplateDialogOpen(true);
    } catch (error: any) {
      console.error('Error loading template:', error);
      setError(error.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while Clerk is initializing
  if (!authLoaded || !userLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
          <Typography variant="h4" gutterBottom>
            Welcome, {user?.firstName || 'User'}!
          </Typography>
          
          {/* Connection Status */}
          <Box sx={{ mb: 3 }}>
            {connectionStatus === 'checking' && (
              <Alert severity="info">Checking backend connection...</Alert>
            )}
            {connectionStatus === 'connected' && (
              <Alert severity="success">Backend connected successfully!</Alert>
            )}
            {connectionStatus === 'error' && (
              <Alert severity="error">
                Backend connection failed. Please check if the backend server is running on port 8000.
              </Alert>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Natural Language Input */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Generate Configuration from Natural Language
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Describe what you need in plain English, e.g., "Configure switch SW-01 with IP 192.168.1.10 and standard VLANs"
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                placeholder="Enter your configuration request..."
                value={naturalLanguageInput}
                onChange={(e) => setNaturalLanguageInput(e.target.value)}
                disabled={loading}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={generateFromNaturalLanguage}
                disabled={loading || !naturalLanguageInput.trim() || connectionStatus !== 'connected'}
                sx={{ minWidth: 120 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generate'}
              </Button>
            </Stack>
          </Paper>

          {/* Test Buttons */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" spacing={2}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={testGenerateConfig}
                disabled={loading || connectionStatus !== 'connected'}
              >
                Test Standard Generation
              </Button>
              <Button 
                variant="outlined" 
                onClick={testToken}
              >
                Debug Token
              </Button>
            </Stack>
          </Box>

          {/* Templates and Configurations Grid */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Templates Card */}
            <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Available Templates ({templates.length})
                    </Typography>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => setCreateTemplateOpen(true)}
                    >
                      Create Template
                    </Button>
                  </Box>
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <Box>
                      {templates.map((template, index) => (
                        <Box 
                          key={index} 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            bgcolor: 'grey.100', 
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'grey.200',
                              transform: 'translateX(4px)'
                            }
                          }}
                          onClick={() => viewTemplate(template.id)}
                        >
                          <Typography variant="subtitle1">{template.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Vendor: {template.vendor} | Type: {template.switch_type}
                          </Typography>
                        </Box>
                      ))}
                      {templates.length === 0 && (
                        <Typography color="text.secondary">No templates available</Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>

            {/* Configurations Card */}
            <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Your Configurations ({configurations.length})
                  </Typography>
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <Box>
                      {configurations.map((config, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="subtitle1">{config.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Vendor: {config.vendor} | Created: {new Date(config.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ))}
                      {configurations.length === 0 && (
                        <Typography color="text.secondary">No configurations yet</Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
          </Stack>

      {/* Configuration Result Dialog */}
      <Dialog open={configDialogOpen} onClose={handleDiscard} maxWidth="md" fullWidth>
        <DialogTitle>
          Generated Configuration - Review Before Saving
        </DialogTitle>
        <DialogContent>
          {generatedConfig && (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Switch: <strong>{generatedConfig.metadata?.switch_name}</strong>
                </Typography>
                <Typography variant="subtitle2">
                  Vendor: <strong>{generatedConfig.metadata?.vendor}</strong>
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Request: "{generatedConfig.metadata?.natural_language_request}"
              </Typography>
              
              {/* Configuration display/edit area */}
              {isEditMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={editableConfig}
                  onChange={(e) => setEditableConfig(e.target.value)}
                  sx={{ 
                    fontFamily: 'monospace',
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      backgroundColor: '#1e1e1e',
                      color: '#00ff00'
                    }
                  }}
                />
              ) : (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.900', borderRadius: 1, position: 'relative' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setIsEditMode(true)}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    Edit
                  </Button>
                  <pre style={{ color: '#00ff00', margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    {editableConfig}
                  </pre>
                </Box>
              )}
              
              {generatedConfig.parsed_parameters && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Parsed Parameters:
                  </Typography>
                  <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {JSON.stringify(generatedConfig.parsed_parameters, null, 2)}
                  </pre>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscard} color="inherit">
            Discard
          </Button>
          {isEditMode && (
            <Button onClick={() => setIsEditMode(false)}>
              Cancel Edit
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary"
            onClick={saveConfiguration}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Viewing Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Template: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Vendor</Typography>
                  <Typography variant="body1">{selectedTemplate.vendor}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body1">{selectedTemplate.switch_type}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Version</Typography>
                  <Typography variant="body1">{selectedTemplate.version}</Typography>
                </Box>
                {selectedTemplate.description && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{selectedTemplate.description}</Typography>
                  </Box>
                )}
              </Stack>
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Template Content
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.900', borderRadius: 1 }}>
                <pre style={{ 
                  color: '#00ff00', 
                  margin: 0, 
                  whiteSpace: 'pre-wrap', 
                  wordWrap: 'break-word',
                  fontSize: '0.875rem'
                }}>
                  {selectedTemplate.template_content}
                </pre>
              </Box>
              
              {selectedTemplate.is_standard && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This is a standard template provided by the system.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={createTemplateOpen} onClose={() => setCreateTemplateOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Create New Jinja2 Template
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Jinja2 Template Guide:</strong>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><code>{`{{ variable }}`}</code> - Replace with actual value (e.g., <code>{`{{ switch_name }}`}</code>)</li>
                <li><code>{`{% if condition %}`}</code> - Include section conditionally</li>
                <li><code>{`{% for item in list %}`}</code> - Loop through items</li>
                <li><code>{`{{ var | default('value') }}`}</code> - Provide default values</li>
              </ul>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Available Variables:</strong> switch_name, management_ip, management_mask, default_gateway, 
              enable_dhcp_snooping, enable_port_security, enable_qos, standard_vlans, custom_vlans, 
              user_email, timestamp, domain_name
            </Typography>
          </Box>

          <Stack spacing={2}>
            <TextField
              label="Template Name"
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
              fullWidth
              required
            />
            
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Vendor"
                value={newTemplate.vendor}
                onChange={(e) => setNewTemplate({...newTemplate, vendor: e.target.value})}
                sx={{ flex: 1 }}
              >
                <MenuItem value="cisco">Cisco</MenuItem>
                <MenuItem value="extreme">Extreme</MenuItem>
              </TextField>
              
              <TextField
                select
                label="Switch Type"
                value={newTemplate.switch_type}
                onChange={(e) => setNewTemplate({...newTemplate, switch_type: e.target.value})}
                sx={{ flex: 1 }}
              >
                <MenuItem value="edge">Edge</MenuItem>
                <MenuItem value="distribution">Distribution</MenuItem>
                <MenuItem value="core">Core</MenuItem>
              </TextField>
              
              <TextField
                label="Version"
                value={newTemplate.version}
                onChange={(e) => setNewTemplate({...newTemplate, version: e.target.value})}
                sx={{ flex: 1 }}
              />
            </Stack>
            
            <TextField
              label="Description"
              value={newTemplate.description}
              onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
              fullWidth
              multiline
              rows={2}
            />
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Template Content (Jinja2 Format)
              </Typography>
              <TextField
                value={newTemplate.template_content}
                onChange={(e) => setNewTemplate({...newTemplate, template_content: e.target.value})}
                fullWidth
                multiline
                rows={12}
                placeholder={`! Cisco Configuration Template
hostname {{ switch_name }}
!
interface Vlan{{ management_vlan | default(1) }}
 ip address {{ management_ip }} {{ management_mask }}
!
{% if enable_dhcp_snooping %}
ip dhcp snooping
{% endif %}`}
                sx={{ 
                  fontFamily: 'monospace',
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Box>
          </Stack>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Example Template Snippet:</strong>
            <pre style={{ margin: '8px 0 0 0', fontSize: '0.75rem' }}>{`{% if standard_vlans %}
! Standard VLANs
vlan 10
 name Data
vlan 20
 name Voice
{% endif %}`}</pre>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTemplateOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              alert('Template creation would be implemented here');
              setCreateTemplateOpen(false);
            }}
            disabled={!newTemplate.name || !newTemplate.template_content}
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;